import { format, parseISO, startOfDay, subDays } from "date-fns"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/dialog"

interface EndDateChangeDialogProps {
  type: "tenant" | "plan"
  open: boolean
  initialDate: string
  nextTenantStartDate: string
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (date: Date) => void
}

export function EndDateChangeDialog({
  type,
  open,
  initialDate,
  nextTenantStartDate,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: EndDateChangeDialogProps) {
  const [date, setDate] = useState<Date>()
  const isTenant = type === "tenant"
  const minimumDate = startOfDay(new Date())
  const maximumDate = subDays(parseISO(nextTenantStartDate), 1)
  const isDateAllowed = !!date && date >= minimumDate && date <= maximumDate

  useEffect(() => {
    if (open) {
      setDate(parseISO(initialDate))
    }
  }, [initialDate, open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] rounded-[10px] p-5">
        <DialogTitle className="text-lg font-semibold text-[#354267]">
          {isTenant ? "退去日の変更" : "終了日の変更"}
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          {isTenant
            ? "次の賃借人の契約開始日以降は選択できません"
            : "次の賃借人の契約期間と重複する日付は選択できません"}
        </DialogDescription>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600" htmlFor="changed-end-date">
            {isTenant ? "変更後の退去日" : "変更後の終了日"}
          </label>
          <DatePicker
            id="changed-end-date"
            value={date}
            onChange={setDate}
            fromDate={minimumDate}
            toDate={maximumDate}
            defaultMonth={date ?? maximumDate}
          />
          <p className="text-xs text-slate-400">
            選択可能な最終日: {format(maximumDate, "yyyy年MM月dd日")}
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            onClick={() => date && isDateAllowed && onSubmit(date)}
            disabled={!isDateAllowed || isSubmitting}
            loading={isSubmitting}
          >
            {isTenant ? "退去日を変更する" : "終了日を変更する"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
